/**
 * La matematica dei KPI energetici del dashboard, in un posto solo.
 *
 * Fin qui viveva in `apps/api/src/lib/dashboard-summary.ts` +
 * `apps/api/src/data/energy.ts`, e il mobile — che non ha un URL API
 * raggiungibile e legge tutto da Supabase — ne teneva una copia a mano in
 * `apps/mobile/src/api/energy.ts`. Due copie della stessa formula restano
 * allineate solo finché qualcuno se ne ricorda: la quota preesistente era già
 * andata in deriva (il mobile usava il pct di configurazione, l'API la misura).
 *
 * Qui la formula è una. `apps/api` la ri-esporta dai suoi moduli storici per non
 * toccare i call-site; il mobile la importa direttamente.
 */

import { eccedenzaPeriodo } from './soglia-incentivabilita';
import { calculateSharedEnergy, gammaAccumulo } from './shared-energy';

/** Riga oraria grezza della RPC `energy_shared`. */
export interface SharedEnergyHourInput {
	/** ISO dell'ora civile italiana, `YYYY-MM-DDTHH:...`. */
	hour: string;
	totalImmessa: number;
	totalPrelevata: number;
	/** Energia assorbita dagli accumuli nell'ora (RPC dal 20260819); assente = 0. */
	totalAssorbita?: number;
}

/** Un'ora della curva di comunità, con l'energia condivisa già calcolata. */
export interface ReconciledSharedHour {
	hour: string;
	totalImmessa: number;
	totalPrelevata: number;
	energiaCondivisa: number;
}

/**
 * La forma minima che serve a {@link periodEnergyTotals}: le tre grandezze
 * orarie, senza vincolare il tipo di `hour` (l'API lo tiene come `Date`).
 */
export interface EnergyHourTotals {
	totalImmessa: number;
	totalPrelevata: number;
	energiaCondivisa: number;
}

/** Parametri della configurazione che decidono la quota incentivabile, in kWh. */
export interface DashboardEnergyConfig {
	/** % preesistente di configurazione (0-100): ripiego quando la produzione
	 * preesistente misurata non è disponibile. */
	preEsistentePct?: number;
	/** % di contributo PNRR (0-100): abbassa la soglia di incentivabilità dal 55% al 45%. */
	pnrrGrantPct?: number;
	/**
	 * Quota (0–1) dell'immessa dagli impianti con contributo in conto capitale:
	 * con questa la soglia vale per insieme di impianti (INB-324). Assente (per
	 * esempio sul mobile, che non ha gli impianti): vale `pnrrGrantPct`.
	 */
	quotaImmessaPnrr?: number | null;
}

/** Totali energetici di una finestra. */
export interface PeriodEnergyTotals {
	productionKwh: number;
	consumptionKwh: number;
	sharedKwh: number;
	incentivizedKwh: number;
	selfConsumptionPct: number | null;
	/** Energia condivisa oltre la soglia di incentivabilità (55%/45% dell'immessa). */
	eccedenzaKwh: number;
	/** Energia condivisa non arrotondata — per la quota energetica del socio. */
	sharedKwhRaw: number;
}

/** Entrata in esercizio prima di questa data → impianto pre-esistente (regola 70/30). */
export const PRE_ESISTENTE_CUTOFF = '2021-12-16';

/**
 * Vero se il più antico impianto del misuratore è anteriore al taglio 70/30.
 * Stessa derivazione di `flattenMeter` in `apps/api/src/data/meters.ts`.
 */
export function isPreEsistente(
	dataEsercizioDates: readonly (string | null | undefined)[],
): boolean {
	const [earliest] = dataEsercizioDates.filter((d): d is string => !!d).sort();
	return earliest !== undefined && earliest < PRE_ESISTENTE_CUTOFF;
}

function round1(value: number): number {
	return Math.round(value * 10) / 10;
}

/**
 * Da righe grezze `energy_shared` a curva di comunità, applicando γ **mese per
 * mese** (γ è una quota mensile: Regole tecniche accumuli, caso ACC/CER,
 * richiamate dalle Regole Operative CACER § 2.2.3). Il denominatore di γ —
 * l'energia prodotta — è approssimato con `immessa + assorbita`: sottostima la
 * produzione di quanto il socio autoconsuma direttamente (vedi
 * `docs/Interpretazioni.md`).
 *
 * È l'**unico** punto in cui `min(immessa, prelevata)` corretto per accumulo
 * viene calcolato: `sharedEnergyPoints` (API) e `fetchReconciledSeries` /
 * `fetchDashboardTotals` (mobile) passano tutti da qui.
 */
export function reconcileSharedHours(
	rows: readonly SharedEnergyHourInput[],
): ReconciledSharedHour[] {
	const perMese = new Map<string, { assorbita: number; prodotta: number }>();
	for (const row of rows) {
		const mese = row.hour.slice(0, 7);
		const acc = perMese.get(mese) ?? { assorbita: 0, prodotta: 0 };
		const assorbita = Number(row.totalAssorbita) || 0;
		acc.assorbita += assorbita;
		acc.prodotta += (Number(row.totalImmessa) || 0) + assorbita;
		perMese.set(mese, acc);
	}

	return rows.map((row) => {
		const mese = perMese.get(row.hour.slice(0, 7));
		const gamma = mese ? gammaAccumulo(mese.assorbita, mese.prodotta) : 0;
		const totalImmessa = Number(row.totalImmessa) || 0;
		const totalPrelevata = Number(row.totalPrelevata) || 0;
		return {
			hour: row.hour,
			totalImmessa,
			totalPrelevata,
			energiaCondivisa: calculateSharedEnergy(totalImmessa, totalPrelevata, { gamma }),
		};
	});
}

/**
 * Somma le ore di una finestra e ne deriva le quote incentivabile ed
 * eccedentaria. La quota preesistente non cambia al cambiare della finestra,
 * quindi resta un parametro e non un secondo calcolo.
 */
export function periodEnergyTotals(
	hours: readonly EnergyHourTotals[],
	preExistingFraction: number,
	config: DashboardEnergyConfig,
): PeriodEnergyTotals {
	const production = hours.reduce((t, h) => t + (Number(h.totalImmessa) || 0), 0);
	const consumption = hours.reduce((t, h) => t + (Number(h.totalPrelevata) || 0), 0);
	const shared = hours.reduce((t, h) => t + (Number(h.energiaCondivisa) || 0), 0);
	const incentivizedKwh = Math.round(shared * (1 - preExistingFraction));

	// Soglia di incentivabilità — 55% standard, 45% col contributo in conto
	// capitale (Art. 8 § 3): quel che la supera è eccedentario. Formula di RO
	// §2.2.2.1.3, per insieme di impianti dove la quota è nota (INB-324).
	const { eccedenzaKwh } = eccedenzaPeriodo({
		energiaIncentivabileKwh: incentivizedKwh,
		energiaImmessaKwh: production,
		hasPnrrGrant: (config.pnrrGrantPct ?? 0) > 0,
		quotaImmessaPnrr: config.quotaImmessaPnrr,
	});

	return {
		productionKwh: round1(production),
		consumptionKwh: round1(consumption),
		sharedKwh: round1(shared),
		incentivizedKwh,
		selfConsumptionPct:
			production > 0 ? Math.round(Math.min(100, (shared / production) * 100)) : null,
		eccedenzaKwh: round1(eccedenzaKwh),
		sharedKwhRaw: shared,
	};
}

/**
 * Quota preesistente del mese: dagli impianti effettivamente marcati tali sui
 * misuratori, non da un pct di configurazione — quel pct resta solo il ripiego
 * per un mese senza produzione misurata. Cap al 30% per la regola 70/30
 * (ALLEGATO 1 § 3.3). Su una configurazione senza impianti preesistenti i due
 * approcci coincidono e `incentivabile === condivisa`.
 */
export function computePreExistingFraction(
	monthSharedEnergy: readonly { totalImmessa: number }[],
	monthSums: readonly { meter_id: string; immessa: number }[],
	metersById: ReadonlyMap<string, { tipo: string; preEsistente?: boolean }>,
	config: DashboardEnergyConfig,
): number {
	const monthProduction = monthSharedEnergy.reduce(
		(total, hour) => total + (Number(hour.totalImmessa) || 0),
		0,
	);

	let preExistingProduction = 0;
	for (const sum of monthSums) {
		const meter = metersById.get(sum.meter_id);
		if (meter?.tipo === 'injection' && meter.preEsistente) {
			preExistingProduction += Number(sum.immessa) || 0;
		}
	}

	return monthProduction > 0
		? Math.min(preExistingProduction / monthProduction, 0.3)
		: Math.min((config.preEsistentePct ?? 0) / 100, 0.3);
}

/**
 * Energia condivisa attribuita al singolo socio: la quota della condivisa di
 * configurazione proporzionale al consumo del socio nel periodo.
 *
 * È il numero che il web espone in `DashboardKpiSummary.members[].sharedMonthKwh`
 * e che il mobile ricalcola dalle sue letture: una formula sola. Senza consumo
 * per socio la condivisa si divide in parti uguali fra i soci attivi.
 */
export function memberSharedEnergy(
	sharedEnergyConfigKwh: number,
	memberConsumptionKwh: number,
	totalConfigConsumptionKwh: number,
	activeMemberCount: number,
): number {
	if (!(sharedEnergyConfigKwh > 0) || activeMemberCount <= 0) return 0;
	if (!(totalConfigConsumptionKwh > 0)) return sharedEnergyConfigKwh / activeMemberCount;
	return sharedEnergyConfigKwh * (memberConsumptionKwh / totalConfigConsumptionKwh);
}
